import { useState } from "react";
import { Loader2, Upload, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";

const emptyForm = {
  full_name: "", phone: "", email: "", location: "", qualification: "",
  experience: "", current_previous_role: "", cover_message: "", consent: false,
};

export const JobApplicationModal = ({ vacancy, open, onClose }) => {
  const [form, setForm] = useState(emptyForm);
  const [cvFile, setCvFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const displayTitle = vacancy && (vacancy.position === "Other" && vacancy.custom_title ? vacancy.custom_title : vacancy.position);

  const reset = () => {
    setForm(emptyForm);
    setCvFile(null);
    setError("");
    setDone(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!cvFile) {
      setError("Please upload your CV / Resume.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const body = new FormData();
      body.append("vacancy_id", vacancy.id);
      Object.entries(form).forEach(([k, v]) => body.append(k, v));
      body.append("cv", cvFile);
      await api.post("/job-applications", body, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not submit your application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!vacancy) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="job-application-modal">
        {done ? (
          <div className="py-6 text-center" data-testid="job-application-success">
            <CheckCircle2 size={40} className="mx-auto text-cts-red mb-4" strokeWidth={1.5} />
            <p className="lead-text">
              Thank you for your application. We have received your details and will review your application.
              If your profile is shortlisted, our team will contact you.
            </p>
            <button onClick={handleClose} data-testid="job-application-close-btn" className="btn-primary mt-6">CLOSE</button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-cts-ink">Apply for: {displayTitle}</DialogTitle>
            </DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4" data-testid="job-application-form">
              {error && <p className="rounded-md bg-cts-redlight px-4 py-3 text-sm text-cts-ink" role="alert" data-testid="job-application-error">{error}</p>}
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="field-label">Full Name *</label>
                  <input required data-testid="jobapp-fullname-input" className="field-input" maxLength={100} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
                </div>
                <div>
                  <label className="field-label">Phone Number *</label>
                  <input required data-testid="jobapp-phone-input" className="field-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                </div>
                <div>
                  <label className="field-label">Email Address *</label>
                  <input required type="email" data-testid="jobapp-email-input" className="field-input" value={form.email} onChange={(e) => set("email", e.target.value)} />
                </div>
                <div>
                  <label className="field-label">Location</label>
                  <input data-testid="jobapp-location-input" className="field-input" value={form.location} onChange={(e) => set("location", e.target.value)} />
                </div>
                <div>
                  <label className="field-label">Qualification</label>
                  <input data-testid="jobapp-qualification-input" className="field-input" value={form.qualification} onChange={(e) => set("qualification", e.target.value)} />
                </div>
                <div>
                  <label className="field-label">Years of Experience</label>
                  <input data-testid="jobapp-experience-input" className="field-input" value={form.experience} onChange={(e) => set("experience", e.target.value)} />
                </div>
                <div>
                  <label className="field-label">Current / Previous Role</label>
                  <input data-testid="jobapp-role-input" className="field-input" value={form.current_previous_role} onChange={(e) => set("current_previous_role", e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <label className="field-label">Cover Message</label>
                  <textarea data-testid="jobapp-cover-message-input" className="field-input" rows={3} maxLength={2000} value={form.cover_message} onChange={(e) => set("cover_message", e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <label className="field-label">Upload CV / Resume *</label>
                  <label className="btn-outline !min-h-[44px] !py-2 cursor-pointer w-fit">
                    <Upload size={15} /> {cvFile ? cvFile.name : "CHOOSE FILE"}
                    <input type="file" accept=".pdf,.doc,.docx" className="hidden" data-testid="jobapp-cv-input" onChange={(e) => setCvFile(e.target.files?.[0] || null)} />
                  </label>
                  <p className="text-xs text-cts-charcoal/60 mt-1">PDF, DOC or DOCX. Max 5MB.</p>
                </div>
                <div className="sm:col-span-2 flex items-start gap-2">
                  <input required type="checkbox" id="jobapp-consent" data-testid="jobapp-consent-checkbox" className="mt-1" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} />
                  <label htmlFor="jobapp-consent" className="text-sm text-cts-charcoal">
                    I consent to CTS collecting and using the information provided in this application for recruitment purposes. *
                  </label>
                </div>
              </div>
              <button type="submit" disabled={submitting} data-testid="jobapp-submit-btn" className="btn-primary disabled:opacity-60 w-full sm:w-auto">
                {submitting ? <Loader2 className="animate-spin" size={16} /> : "SUBMIT APPLICATION"}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
